import { XMarkIcon } from '@heroicons/react/24/outline';
import { CogIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef } from 'react';
import { useClickAway, useIntersection } from 'react-use';
import { SideNavItem } from './SideNav';
import { SideNavElement } from './SideNavElement';
import { SIDE_NAME_ICON_CONFIG } from './config';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { navItemLabel } from 'api/nav-items';

export const SubItemsSideNav = ({
  activeSubMenu,
  setActiveSubMenu,
  allowHoverSubMenu,
  items,
  setAllowHoverSubMenu,
}: {
  activeSubMenu?: string;
  setActiveSubMenu: (value?: string) => void;
  setOpenSideBar: (value: boolean) => void;
  allowHoverSubMenu?: string;
  items: SideNavItem[];
  setAllowHoverSubMenu: (value?: string) => void;
}) => {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  const item = items.find((x) => x.key === activeSubMenu);
  const Icon = SIDE_NAME_ICON_CONFIG[item?.icon || ''] || CogIcon;

  const subItems = item?.subItems || [];
  const isCustomReportsSubNav = item?.key === 'pinned-custom-reports';

  useClickAway(ref, () => {
    if (!allowHoverSubMenu) {
      setActiveSubMenu();
    }
  });

  const intersectionRef = React.useRef<HTMLAnchorElement>(null);
  const intersection = useIntersection(
    intersectionRef as RefObject<HTMLElement>, // TODO: Fix this when react-use is updated to support React 19 properly
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.8,
    },
  );

  return (
    <AnimatePresence>
      {activeSubMenu && (
        <motion.nav
          ref={ref}
          initial={{
            x: -250,
          }}
          animate={{
            x: 0,
          }}
          exit={{
            x: -250,
          }}
          transition={{
            duration: 0.2,
          }}
          className={`absolute top-0 left-[100%] z-[50] flex h-full max-h-[calc(100vh-48px)] w-[250px] flex-col gap-y-2 border-r border-gray-300 bg-white p-2 text-center lg:mt-0 lg:max-w-none dark:border-gray-700 dark:bg-gray-900`}
        >
          <div
            className={`relative flex items-center gap-2 overflow-hidden border-b border-gray-300 p-2 pb-3 text-sm whitespace-nowrap text-gray-600 dark:border-gray-700 dark:text-gray-400`}
          >
            <Icon className={`h-5 w-5 flex-shrink-0`} />
            {item && navItemLabel(t, item.name)}
            <button
              className="ml-auto"
              onClick={() => {
                setActiveSubMenu(undefined);
                setAllowHoverSubMenu(undefined);
              }}
            >
              <XMarkIcon className="ml-auto h-4 w-4 text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" />
            </button>
          </div>
          <div className="scrollbar-hide flex h-screen flex-col gap-y-2 overflow-y-scroll">
            {item?.subItems?.map((item, index) => {
              return (
                <SideNavElement
                  item={item}
                  key={item.key}
                  isExpanded={true}
                  linkRef={
                    index === subItems?.length - 1 ? intersectionRef : undefined
                  }
                />
              );
            })}
            <div
              className={`pointer-events-none fixed right-0 bottom-[4px] left-[235px] h-6 w-[220px] bg-gradient-to-t from-gray-200 via-transparent to-transparent transition-all duration-200 ease-out dark:from-gray-900 ${
                !intersection?.isIntersecting && isCustomReportsSubNav
                  ? 'opacity-90'
                  : 'opacity-0'
              }`}
            ></div>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};
